import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getApiBase } from '../apiBase';
import { authFetch, getUsername, isCollector, mustUseTodayRecordDate } from '../auth';
import { modalPanelClass } from './tableToolbar';
import { getPaymentCheques, getPaymentCdmDeposits, getPaymentOnlineTransfers } from './paymentCheques';
import { SRI_LANKA_BANKS, bankCodeForName } from './sriLankaBanks';
import { useCollectorCollectionClosed } from './collectionDayClose';
import {
  normalizePaymentReceiptInput,
  suggestNextPaymentReceiptNumber,
} from './paymentReceipt';
import {
  billDetailsLine,
  buildCustomerOutstandingBills,
  openingBalanceBillDate,
  openingBalanceBillId,
} from './pendingBills';

const apiBase = getApiBase();

function money(n) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'LKR',
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function todayYmdLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

let chequeKeySeq = 0;
function newChequeLine(overrides = {}) {
  chequeKeySeq += 1;
  return {
    key: `chq-${chequeKeySeq}`,
    id: '',
    amount: '',
    chequeDate: todayYmdLocal(),
    chequeNumber: '',
    chequeBank: '',
    chequeBankCode: '',
    chequeBranchCode: '',
    chequeDeposited: false,
    ...overrides,
  };
}

let cdmKeySeq = 0;
function newCdmLine(overrides = {}) {
  cdmKeySeq += 1;
  return {
    key: `cdm-${cdmKeySeq}`,
    id: '',
    amount: '',
    cdmNumber: '',
    cdmDate: todayYmdLocal(),
    bankAccountId: '',
    ...overrides,
  };
}

let onlineTransferKeySeq = 0;
function newOnlineTransferLine(overrides = {}) {
  onlineTransferKeySeq += 1;
  return {
    key: `ot-${onlineTransferKeySeq}`,
    id: '',
    amount: '',
    reference: '',
    transferDate: todayYmdLocal(),
    bankAccountId: '',
    ...overrides,
  };
}

function bankAccountOptionLabel(a) {
  const nick = String(a.nickName ?? '').trim() || 'Account';
  const detail = [a.bank, a.accountNumber].map((x) => String(x ?? '').trim()).filter(Boolean).join(' · ');
  return detail ? `${nick} — ${detail}` : nick;
}

/** Last cheque details from the customer's most recent payment (excludes cheque number). */
function lastChequeDefaultsForCustomer(payments, customerId) {
  const cid = String(customerId ?? '').trim();
  if (!cid) return null;
  const sorted = (Array.isArray(payments) ? payments : [])
    .filter((p) => String(p.customerId ?? '').trim() === cid)
    .sort((a, b) =>
      String(b.createdAt || `${b.date}T23:59:59`).localeCompare(
        String(a.createdAt || `${a.date}T23:59:59`),
      ),
    );
  for (const payment of sorted) {
    const cheques = getPaymentCheques(payment);
    if (cheques.length === 0) continue;
    const last = cheques[cheques.length - 1];
    return {
      chequeBank: last.chequeBank || '',
      chequeBankCode: last.chequeBankCode || '',
      chequeBranchCode: last.chequeBranchCode || '',
      chequeDate: last.chequeDate || todayYmdLocal(),
    };
  }
  return null;
}

function lastOtherMethodBankDefaults(payments, customerId) {
  const cid = String(customerId ?? '').trim();
  if (!cid) return { cdmBankAccountId: '', onlineTransferBankAccountId: '' };
  const sorted = (Array.isArray(payments) ? payments : [])
    .filter((p) => String(p.customerId ?? '').trim() === cid)
    .sort((a, b) =>
      String(b.createdAt || `${b.date}T23:59:59`).localeCompare(
        String(a.createdAt || `${a.date}T23:59:59`),
      ),
    );
  let cdmBankAccountId = '';
  let onlineTransferBankAccountId = '';
  for (const payment of sorted) {
    if (!cdmBankAccountId) {
      const deposits = getPaymentCdmDeposits(payment);
      const last = deposits[deposits.length - 1];
      if (last?.bankAccountId) cdmBankAccountId = last.bankAccountId;
    }
    if (!onlineTransferBankAccountId) {
      const transfers = getPaymentOnlineTransfers(payment);
      const last = transfers[transfers.length - 1];
      if (last?.bankAccountId) onlineTransferBankAccountId = last.bankAccountId;
    }
    if (cdmBankAccountId && onlineTransferBankAccountId) break;
  }
  return { cdmBankAccountId, onlineTransferBankAccountId };
}

const emptyForm = (receiptNumber = '') => ({
  customerId: '',
  billNumber: receiptNumber,
  appliedBillIds: [],
  cashAmount: '',
  cdmDeposits: [newCdmLine()],
  onlineTransfers: [newOnlineTransferLine()],
  cheques: [newChequeLine()],
  date: todayYmdLocal(),
  note: '',
});

function formFromPayment(payment) {
  const chequeRows = getPaymentCheques(payment);
  const cheques =
    chequeRows.length > 0
      ? chequeRows.map((c) =>
          newChequeLine({
            id: c.id || '',
            amount: String(c.amount),
            chequeDate: c.chequeDate || todayYmdLocal(),
            chequeNumber: c.chequeNumber,
            chequeBank: c.chequeBank || '',
            chequeBankCode: c.chequeBankCode || '',
            chequeBranchCode: c.chequeBranchCode || '',
            chequeDeposited: c.chequeDeposited,
          }),
        )
      : [newChequeLine()];
  const cdmRows = getPaymentCdmDeposits(payment);
  const cdmDeposits =
    cdmRows.length > 0
      ? cdmRows.map((d) =>
          newCdmLine({
            id: d.id === '_legacy' ? '' : d.id || '',
            amount: String(d.amount),
            cdmNumber: d.cdmNumber || '',
            cdmDate: d.cdmDate || payment.date || todayYmdLocal(),
            bankAccountId: d.bankAccountId || '',
          }),
        )
      : [newCdmLine()];
  const onlineRows = getPaymentOnlineTransfers(payment);
  const onlineTransfers =
    onlineRows.length > 0
      ? onlineRows.map((t) =>
          newOnlineTransferLine({
            id: t.id === '_legacy' ? '' : t.id || '',
            amount: String(t.amount),
            reference: t.reference || '',
            transferDate: t.transferDate || payment.date || todayYmdLocal(),
            bankAccountId: t.bankAccountId || '',
          }),
        )
      : [newOnlineTransferLine()];
  const billNumber = String(payment.billNumber ?? '').trim();
  const appliedBillIds = Array.isArray(payment.appliedBillIds)
    ? payment.appliedBillIds.map(String)
    : Array.isArray(payment.appliedBills)
      ? payment.appliedBills.map((b) => String(b.id ?? '')).filter(Boolean)
      : [];
  return {
    customerId: payment.customerId || '',
    billNumber,
    appliedBillIds,
    cashAmount: payment.cashAmount != null && payment.cashAmount !== '' ? String(payment.cashAmount) : '',
    cdmDeposits,
    onlineTransfers,
    cheques,
    date: payment.date || todayYmdLocal(),
    note: payment.note || '',
  };
}

export default function RecordPaymentModal({
  open,
  onClose,
  onSaved,
  prefillCustomerId = '',
  editPayment = null,
  lockCustomer = false,
  customerName = '',
}) {
  const receiptNumberTouched = useRef(false);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const paymentDay = useMemo(() => todayYmdLocal(), []);
  const { closed: collectionClosed } = useCollectorCollectionClosed(paymentDay);
  const newPaymentsLocked = isCollector() && collectionClosed && !editPayment;

  const loadBills = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/bills`);
      if (!res.ok) throw new Error('Failed to load bills');
      const data = await res.json();
      setBills(Array.isArray(data) ? data : []);
    } catch {
      setBills([]);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/customers`);
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch {
      setCustomers([]);
    }
  }, []);

  const loadPayments = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/payments`);
      if (!res.ok) throw new Error('Failed to load payments');
      const data = await res.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      setPayments([]);
    }
  }, []);

  const loadBankAccounts = useCallback(async () => {
    try {
      const res = await authFetch(`${apiBase}/api/shop`);
      if (!res.ok) throw new Error('Failed to load shop');
      const data = await res.json();
      setBankAccounts(Array.isArray(data?.bankAccounts) ? data.bankAccounts : []);
    } catch {
      setBankAccounts([]);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    receiptNumberTouched.current = false;
    setSaveError(null);
    loadCustomers();
    loadBills();
    loadPayments();
    loadBankAccounts();
  }, [open, loadCustomers, loadBills, loadPayments, loadBankAccounts]);

  useEffect(() => {
    if (!open) return;
    if (editPayment) {
      setForm(formFromPayment(editPayment));
      return;
    }
    const defaults = prefillCustomerId
      ? lastChequeDefaultsForCustomer(payments, prefillCustomerId)
      : null;
    const bankDefaults = prefillCustomerId
      ? lastOtherMethodBankDefaults(payments, prefillCustomerId)
      : { cdmBankAccountId: '', onlineTransferBankAccountId: '' };
    setForm({
      ...emptyForm(suggestNextPaymentReceiptNumber(payments)),
      customerId: prefillCustomerId || '',
      cdmDeposits: [newCdmLine({ bankAccountId: bankDefaults.cdmBankAccountId })],
      onlineTransfers: [newOnlineTransferLine({ bankAccountId: bankDefaults.onlineTransferBankAccountId })],
      cheques: [newChequeLine(defaults || {})],
    });
  }, [open, editPayment, prefillCustomerId, payments]);

  useEffect(() => {
    if (!open || editPayment || receiptNumberTouched.current) return;
    const next = suggestNextPaymentReceiptNumber(payments);
    setForm((f) => (f.billNumber === next ? f : { ...f, billNumber: next }));
  }, [open, editPayment, payments]);

  const lockDateToToday = mustUseTodayRecordDate();

  const handleChange = (field, value) => {
    if (field === 'date' && lockDateToToday) return;
    if (field === 'billNumber') {
      receiptNumberTouched.current = true;
      setForm((f) => ({ ...f, billNumber: String(value).slice(0, 40) }));
      return;
    }
    if (field === 'customerId') {
      const defaults = lastChequeDefaultsForCustomer(payments, value);
      const bankDefaults = lastOtherMethodBankDefaults(payments, value);
      setForm((f) => ({
        ...f,
        customerId: value,
        appliedBillIds: [],
        cdmDeposits: [
          newCdmLine({ bankAccountId: bankDefaults.cdmBankAccountId || f.cdmDeposits[0]?.bankAccountId || '' }),
        ],
        onlineTransfers: [
          newOnlineTransferLine({
            bankAccountId: bankDefaults.onlineTransferBankAccountId || f.onlineTransfers[0]?.bankAccountId || '',
          }),
        ],
        cheques: [newChequeLine(defaults || {})],
      }));
      return;
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const toggleAppliedBill = (billId) => {
    const id = String(billId ?? '').trim();
    if (!id) return;
    setForm((f) => {
      const set = new Set(f.appliedBillIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...f, appliedBillIds: [...set] };
    });
  };

  const customerBillOptions = useMemo(() => {
    if (!form.customerId) return [];
    const outstanding = buildCustomerOutstandingBills(customers, bills, payments, form.customerId, {
      excludePaymentId: editPayment?.id || null,
    });
    const byId = new Map(outstanding.map((r) => [r.id, r]));
    for (const id of form.appliedBillIds) {
      if (byId.has(id)) continue;
      const bill = bills.find((b) => b.id === id);
      if (bill) {
        byId.set(id, {
          id: bill.id,
          billDate: bill.date,
          outstandingAmount: 0,
          billTotal: Number(bill.totalAmount) || 0,
          details: billDetailsLine(bill),
        });
        continue;
      }
      const cust = customers.find((c) => String(c.id ?? '').trim() === String(form.customerId ?? '').trim());
      if (cust && id === openingBalanceBillId(cust.id)) {
        byId.set(id, {
          id,
          isOpeningBalance: true,
          billDate: openingBalanceBillDate(cust),
          outstandingAmount: 0,
          billTotal: Number(cust.pastBill) || 0,
          details: 'Opening balance',
        });
      }
    }
    return [...byId.values()].sort((a, b) => {
      if (Boolean(a.isOpeningBalance) !== Boolean(b.isOpeningBalance)) {
        return a.isOpeningBalance ? -1 : 1;
      }
      return String(a.billDate).localeCompare(String(b.billDate));
    });
  }, [form.customerId, form.appliedBillIds, customers, bills, payments, editPayment?.id]);

  const handleChequeChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      cheques: f.cheques.map((c) => {
        if (c.key !== key) return c;
        const next = { ...c, [field]: value };
        if (field === 'chequeBank') {
          const code = bankCodeForName(value);
          if (code) next.chequeBankCode = code;
        }
        return next;
      }),
    }));
  };

  const addChequeLine = () => {
    setForm((f) => {
      const template = f.cheques[0];
      const defaults = template
        ? {
            chequeBank: template.chequeBank,
            chequeBankCode: template.chequeBankCode,
            chequeBranchCode: template.chequeBranchCode,
            chequeDate: template.chequeDate,
          }
        : {};
      return { ...f, cheques: [...f.cheques, newChequeLine(defaults)] };
    });
  };

  const removeChequeLine = (key) => {
    setForm((f) => {
      const next = f.cheques.filter((c) => c.key !== key);
      return { ...f, cheques: next.length > 0 ? next : [newChequeLine()] };
    });
  };

  const handleCdmChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      cdmDeposits: f.cdmDeposits.map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const addCdmLine = () => {
    setForm((f) => {
      const template = f.cdmDeposits[0];
      return {
        ...f,
        cdmDeposits: [...f.cdmDeposits, newCdmLine({ bankAccountId: template?.bankAccountId || '', cdmDate: template?.cdmDate || todayYmdLocal() })],
      };
    });
  };

  const removeCdmLine = (key) => {
    setForm((f) => {
      const next = f.cdmDeposits.filter((line) => line.key !== key);
      return { ...f, cdmDeposits: next.length > 0 ? next : [newCdmLine()] };
    });
  };

  const handleOnlineTransferChange = (key, field, value) => {
    setForm((f) => ({
      ...f,
      onlineTransfers: f.onlineTransfers.map((line) => (line.key === key ? { ...line, [field]: value } : line)),
    }));
  };

  const addOnlineTransferLine = () => {
    setForm((f) => {
      const template = f.onlineTransfers[0];
      return {
        ...f,
        onlineTransfers: [
          ...f.onlineTransfers,
          newOnlineTransferLine({
            bankAccountId: template?.bankAccountId || '',
            transferDate: template?.transferDate || todayYmdLocal(),
          }),
        ],
      };
    });
  };

  const removeOnlineTransferLine = (key) => {
    setForm((f) => {
      const next = f.onlineTransfers.filter((line) => line.key !== key);
      return { ...f, onlineTransfers: next.length > 0 ? next : [newOnlineTransferLine()] };
    });
  };

  const chequeTotalPreview = form.cheques.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const cdmTotalPreview = form.cdmDeposits.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const onlineTransferTotalPreview = form.onlineTransfers.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const paymentTotalPreview =
    (Number(form.cashAmount) || 0) + chequeTotalPreview + cdmTotalPreview + onlineTransferTotalPreview;

  const lockedCustomerLabel =
    customerName ||
    customers.find((c) => c.id === form.customerId)?.name ||
    '—';

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = getUsername();
    if (!username) {
      setSaveError('You need to be signed in with a username.');
      return;
    }
    if (newPaymentsLocked) {
      setSaveError('Collection for this day is over. You cannot add more payments.');
      return;
    }
    if (!form.customerId) {
      setSaveError('Select a customer.');
      return;
    }
    const receiptNumber = normalizePaymentReceiptInput(form.billNumber);
    if (!receiptNumber) {
      setSaveError('Enter a payment receipt # (letters and/or numbers).');
      return;
    }
    if (!/^[A-Za-z0-9][A-Za-z0-9 \-._/]*$/.test(receiptNumber)) {
      setSaveError('Receipt # can use letters, numbers, spaces, and . _ - /');
      return;
    }
    const cash = Number(form.cashAmount) || 0;
    const cdmLines = [];
    for (let i = 0; i < form.cdmDeposits.length; i++) {
      const line = form.cdmDeposits[i];
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!String(line.cdmNumber).trim()) {
        setSaveError(`CDM deposit ${i + 1}: enter a CDM number.`);
        return;
      }
      if (!String(line.bankAccountId).trim()) {
        setSaveError(`CDM deposit ${i + 1}: select a bank account.`);
        return;
      }
      if (!line.cdmDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.cdmDate)) {
        setSaveError(`CDM deposit ${i + 1}: enter a valid deposit date.`);
        return;
      }
      const entry = {
        amount,
        cdmNumber: String(line.cdmNumber).trim(),
        cdmDate: line.cdmDate,
        bankAccountId: String(line.bankAccountId).trim(),
      };
      if (line.id) entry.id = line.id;
      cdmLines.push(entry);
    }
    const onlineLines = [];
    for (let i = 0; i < form.onlineTransfers.length; i++) {
      const line = form.onlineTransfers[i];
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!String(line.reference).trim()) {
        setSaveError(`Online transfer ${i + 1}: enter a transfer reference number.`);
        return;
      }
      if (!String(line.bankAccountId).trim()) {
        setSaveError(`Online transfer ${i + 1}: select a bank account.`);
        return;
      }
      if (!line.transferDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.transferDate)) {
        setSaveError(`Online transfer ${i + 1}: enter a valid transfer date.`);
        return;
      }
      const entry = {
        amount,
        reference: String(line.reference).trim(),
        transferDate: line.transferDate,
        bankAccountId: String(line.bankAccountId).trim(),
      };
      if (line.id) entry.id = line.id;
      onlineLines.push(entry);
    }
    const cdm = cdmLines.reduce((s, d) => s + d.amount, 0);
    const onlineTransfer = onlineLines.reduce((s, t) => s + t.amount, 0);
    const chequeLines = [];
    for (let i = 0; i < form.cheques.length; i++) {
      const line = form.cheques[i];
      if (line.chequeDeposited) {
        const amount = Number(line.amount) || 0;
        if (amount <= 0) continue;
        const entry = {
          amount,
          chequeDate: line.chequeDate,
          chequeNumber: String(line.chequeNumber).trim(),
          chequeBank: String(line.chequeBank).trim(),
          chequeBankCode: String(line.chequeBankCode).trim(),
          chequeBranchCode: String(line.chequeBranchCode).trim(),
        };
        if (line.id) entry.id = line.id;
        chequeLines.push(entry);
        continue;
      }
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      if (!line.chequeDate || !/^\d{4}-\d{2}-\d{2}$/.test(line.chequeDate)) {
        setSaveError(`Cheque ${i + 1}: enter a valid conversion date.`);
        return;
      }
      if (!String(line.chequeNumber).trim()) {
        setSaveError(`Cheque ${i + 1}: enter a cheque number.`);
        return;
      }
      const entry = {
        amount,
        chequeDate: line.chequeDate,
        chequeNumber: String(line.chequeNumber).trim(),
        chequeBank: String(line.chequeBank).trim(),
        chequeBankCode: String(line.chequeBankCode).trim(),
        chequeBranchCode: String(line.chequeBranchCode).trim(),
      };
      if (line.id) entry.id = line.id;
      chequeLines.push(entry);
    }
    const chequeTotal = chequeLines.reduce((s, c) => s + c.amount, 0);
    if (cash <= 0 && chequeTotal <= 0 && cdm <= 0 && onlineTransfer <= 0) {
      setSaveError('Enter a cash, cheque, CDM deposit, and/or online transfer amount so the total is greater than 0.');
      return;
    }
    if (
      payments.some(
        (r) => String(r.billNumber || '').trim() === receiptNumber && r.id !== editPayment?.id,
      )
    ) {
      setSaveError('This payment receipt number is already used.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        customerId: form.customerId,
        billNumber: receiptNumber,
        appliedBillIds: form.appliedBillIds,
        cashAmount: cash,
        cdmDeposits: cdmLines,
        onlineTransfers: onlineLines,
        cheques: chequeLines,
        date: lockDateToToday ? todayYmdLocal() : form.date,
        note: form.note.trim(),
      };
      const isEdit = !!editPayment?.id;
      const res = await authFetch(
        isEdit ? `${apiBase}/api/payments/${encodeURIComponent(editPayment.id)}` : `${apiBase}/api/payments`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            isEdit ? { ...payload, updatedBy: username } : { ...payload, recordedBy: username },
          ),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || 'Save failed');
        return;
      }
      if (onSaved) await onSaved(data);
      onClose();
    } catch {
      setSaveError('Could not reach the server.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-payment-modal-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={`${modalPanelClass} flex max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-bottom,0px)))] w-full max-w-none flex-col overflow-hidden !p-0 sm:max-w-xl lg:max-w-4xl`}
      >
        <div className="shrink-0 border-b border-slate-100 px-4 pb-3 pt-3 sm:px-6 sm:pt-6">
          <div
            className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-slate-300/90 sm:hidden"
            aria-hidden
          />
          <h2 id="record-payment-modal-title" className="text-lg font-bold text-slate-900 sm:text-xl">
            {editPayment ? 'Edit payment' : 'Record payment'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Logged in as {getUsername() || '—'}.
          </p>
        </div>
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6">
            <div className="space-y-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-8 lg:gap-y-4 lg:space-y-0">
              {newPaymentsLocked ? (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-950 ring-1 ring-amber-100 lg:col-span-2" role="status">
                  Collection for today is over. You cannot add more payments.
                </p>
              ) : null}
              {saveError ? (
                <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800 ring-1 ring-red-100 lg:col-span-2">
                  {saveError}
                </p>
              ) : null}
              <div className="min-w-0 space-y-4">
                {lockCustomer ? (
                  <div className="block text-sm font-medium text-slate-600">
                    Customer
                    <p className="mt-1 rounded-xl bg-slate-100 px-3 py-2.5 text-sm font-semibold text-slate-900 ring-1 ring-slate-200">
                      {lockedCustomerLabel}
                    </p>
                  </div>
                ) : (
                  <label className="block text-sm font-medium text-slate-600">
                    Customer
                    <select
                      required
                      value={form.customerId}
                      onChange={(e) => handleChange('customerId', e.target.value)}
                      className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                      disabled={customers.length === 0}
                    >
                      <option value="">{customers.length === 0 ? 'No customers yet' : 'Select customer…'}</option>
                      {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {form.customerId ? (
                  <fieldset className="rounded-xl bg-slate-50/90 p-3 ring-1 ring-slate-100 sm:p-4">
                    <legend className="px-1 text-sm font-semibold text-slate-800">
                      Credit bills this payment is for
                    </legend>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">
                      Optional. Select opening balance (shown as an invoice) and/or outstanding bills. Customer balances still follow
                      opening balance first, then oldest bills, unless you allocate amounts per invoice.
                    </p>
                    {customerBillOptions.length === 0 ? (
                      <p className="mt-3 text-sm text-slate-500">No outstanding credit bills for this customer.</p>
                    ) : (
                      <ul className="mt-3 max-h-[min(36vh,14rem)] space-y-2 overflow-y-auto overscroll-contain sm:max-h-48 lg:max-h-[min(52vh,22rem)]">
                        {customerBillOptions.map((b) => {
                          const checked = form.appliedBillIds.includes(b.id);
                          return (
                            <li key={b.id}>
                              <button
                                type="button"
                                onClick={() => toggleAppliedBill(b.id)}
                                className={`flex w-full cursor-pointer gap-3 rounded-lg px-3 py-3 text-left text-sm ring-1 transition sm:py-2.5 ${
                                  checked
                                    ? 'bg-indigo-50 ring-indigo-200'
                                    : 'bg-white ring-slate-200 hover:bg-slate-50'
                                }`}
                              >
                                <span className="min-w-0 flex-1">
                                  <span className="flex flex-col gap-0.5 sm:block">
                                    <span className="font-medium tabular-nums text-slate-900">
                                      {b.isOpeningBalance
                                        ? 'Opening balance'
                                        : b.billDate || '—'}
                                      {b.isOpeningBalance ? (
                                        <span className="ml-2 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                                          Invoice
                                        </span>
                                      ) : null}
                                    </span>
                                    <span className="font-semibold tabular-nums text-emerald-800 sm:ml-2 sm:inline">
                                      {b.outstandingAmount > 0
                                        ? `${money(b.outstandingAmount)} due`
                                        : 'Settled'}
                                      {b.billTotal > 0 ? (
                                        <span className="ml-1 text-xs font-normal text-slate-500 sm:ml-2">
                                          of {money(b.billTotal)}
                                        </span>
                                      ) : null}
                                    </span>
                                  </span>
                                  <span className="mt-1 block text-xs leading-snug text-slate-600">{b.details}</span>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {form.appliedBillIds.length > 0 ? (
                      <p className="mt-2 text-xs font-medium text-indigo-700">
                        {form.appliedBillIds.length} bill{form.appliedBillIds.length === 1 ? '' : 's'} selected
                      </p>
                    ) : null}
                  </fieldset>
                ) : null}
              </div>
              <div className="min-w-0 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm font-medium text-slate-600">
                    Payment receipt #
                    <input
                      type="text"
                      autoComplete="off"
                      required
                      maxLength={40}
                      value={form.billNumber}
                      onChange={(e) => handleChange('billNumber', e.target.value)}
                      className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                      placeholder="e.g. PAY-012 or 013"
                    />
                  </label>
                  <label className="block text-sm font-medium text-slate-600">
                    Payment date
                    {lockDateToToday ? (
                      <>
                        <p
                          className="mt-1 flex min-h-[2.75rem] items-center rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm tabular-nums text-slate-700 ring-1 ring-slate-200"
                          aria-readonly="true"
                        >
                          {todayYmdLocal()}
                          <span className="sr-only"> (today, cannot be changed)</span>
                        </p>
                        <span className="mt-1 block text-xs font-normal text-slate-500">
                          Today only — this date cannot be changed.
                        </span>
                      </>
                    ) : (
                      <input
                        type="date"
                        required
                        value={form.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                      />
                    )}
                  </label>
                </div>
                <p className="text-xs font-normal text-slate-500">
                  {editPayment
                    ? 'Receipt # is unique across payments. Change either field if needed.'
                    : 'Receipt # is filled from the last saved payment (+1). You can change it before saving.'}
                </p>
                <label className="block text-sm font-medium text-slate-600">
                  Cash (LKR)
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.cashAmount}
                    onChange={(e) => handleChange('cashAmount', e.target.value)}
                    className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                    placeholder="0"
                  />
                </label>
                <fieldset className="rounded-xl bg-sky-50/70 p-3 ring-1 ring-sky-100 sm:p-4">
                  <legend className="px-1 text-sm font-semibold text-slate-800">CDM deposits</legend>
                  <div className="mt-1 flex flex-wrap items-start justify-between gap-2">
                    <p className="text-xs text-slate-500">
                      Enter amount, deposit date, CDM number, and the shop bank account. Credited when a manager approves.
                    </p>
                    <button
                      type="button"
                      onClick={addCdmLine}
                      className="shrink-0 rounded-lg border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-sky-800 shadow-sm transition hover:bg-sky-50"
                    >
                      + Add CDM
                    </button>
                  </div>
                  <div className="mt-3 space-y-3">
                    {form.cdmDeposits.map((line, index) => (
                      <div key={line.key} className="rounded-xl bg-white/80 p-3 ring-1 ring-sky-100 sm:p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            CDM {index + 1}
                          </p>
                          {form.cdmDeposits.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeCdmLine(line.key)}
                              className="text-xs font-medium text-slate-500 hover:text-rose-600"
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm font-medium text-slate-600">
                            Amount (LKR)
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={line.amount}
                              onChange={(e) => handleCdmChange(line.key, 'amount', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="0"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600">
                            Deposit date <span className="text-rose-600">*</span>
                            <input
                              type="date"
                              value={line.cdmDate}
                              onChange={(e) => handleCdmChange(line.key, 'cdmDate', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                            CDM number <span className="text-rose-600">*</span>
                            <input
                              type="text"
                              autoComplete="off"
                              value={line.cdmNumber}
                              onChange={(e) => handleCdmChange(line.key, 'cdmNumber', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="e.g. CDM receipt / slip no."
                            />
                          </label>
                        </div>
                        <label className="mt-3 block text-sm font-medium text-slate-600">
                          Bank account <span className="text-rose-600">*</span>
                          <select
                            value={line.bankAccountId}
                            onChange={(e) => handleCdmChange(line.key, 'bankAccountId', e.target.value)}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            disabled={bankAccounts.length === 0}
                          >
                            <option value="">
                              {bankAccounts.length === 0 ? 'No accounts — add under Shop' : 'Select account…'}
                            </option>
                            {bankAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {bankAccountOptionLabel(a)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
                <fieldset className="rounded-xl bg-teal-50/70 p-3 ring-1 ring-teal-100 sm:p-4">
                  <legend className="px-1 text-sm font-semibold text-slate-800">Online transfers</legend>
                  <div className="mt-1 flex flex-wrap items-start justify-between gap-2">
                    <p className="text-xs text-slate-500">
                      Enter amount, transfer date, bank reference, and the shop bank account. Credited when a manager approves.
                    </p>
                    <button
                      type="button"
                      onClick={addOnlineTransferLine}
                      className="shrink-0 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-semibold text-teal-800 shadow-sm transition hover:bg-teal-50"
                    >
                      + Add transfer
                    </button>
                  </div>
                  <div className="mt-3 space-y-3">
                    {form.onlineTransfers.map((line, index) => (
                      <div key={line.key} className="rounded-xl bg-white/80 p-3 ring-1 ring-teal-100 sm:p-4">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Transfer {index + 1}
                          </p>
                          {form.onlineTransfers.length > 1 ? (
                            <button
                              type="button"
                              onClick={() => removeOnlineTransferLine(line.key)}
                              className="text-xs font-medium text-slate-500 hover:text-rose-600"
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block text-sm font-medium text-slate-600">
                            Amount (LKR)
                            <input
                              type="number"
                              min={0}
                              step={0.01}
                              value={line.amount}
                              onChange={(e) => handleOnlineTransferChange(line.key, 'amount', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="0"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600">
                            Transfer date <span className="text-rose-600">*</span>
                            <input
                              type="date"
                              value={line.transferDate}
                              onChange={(e) => handleOnlineTransferChange(line.key, 'transferDate', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            />
                          </label>
                          <label className="block text-sm font-medium text-slate-600 sm:col-span-2">
                            Transfer reference # <span className="text-rose-600">*</span>
                            <input
                              type="text"
                              autoComplete="off"
                              value={line.reference}
                              onChange={(e) => handleOnlineTransferChange(line.key, 'reference', e.target.value)}
                              className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                              placeholder="e.g. bank ref / transaction ID"
                            />
                          </label>
                        </div>
                        <label className="mt-3 block text-sm font-medium text-slate-600">
                          Bank account <span className="text-rose-600">*</span>
                          <select
                            value={line.bankAccountId}
                            onChange={(e) => handleOnlineTransferChange(line.key, 'bankAccountId', e.target.value)}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                            disabled={bankAccounts.length === 0}
                          >
                            <option value="">
                              {bankAccounts.length === 0 ? 'No accounts — add under Shop' : 'Select account…'}
                            </option>
                            {bankAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {bankAccountOptionLabel(a)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ))}
                  </div>
                </fieldset>
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-800">Cheques</p>
                    <button
                      type="button"
                      onClick={addChequeLine}
                      className="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                    >
                      + Add cheque
                    </button>
                  </div>
                  {form.cheques.map((line, index) => (
                    <div
                      key={line.key}
                      className="rounded-xl bg-slate-50/90 p-4 ring-1 ring-slate-100"
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Cheque {index + 1}
                          {line.chequeDeposited ? (
                            <span className="ml-2 normal-case text-emerald-700">(deposited)</span>
                          ) : null}
                        </p>
                        {form.cheques.length > 1 && !line.chequeDeposited ? (
                          <button
                            type="button"
                            onClick={() => removeChequeLine(line.key)}
                            className="text-xs font-medium text-slate-500 hover:text-rose-600"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                      {line.chequeDeposited ? (
                        <p className="mb-3 text-xs text-slate-500">
                          This cheque is already marked as deposited and cannot be changed here.
                        </p>
                      ) : null}
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block text-sm font-medium text-slate-600">
                          Amount (LKR)
                          <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={line.amount}
                            onChange={(e) => handleChequeChange(line.key, 'amount', e.target.value)}
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm tabular-nums ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="0"
                          />
                        </label>
                        <label className="block text-sm font-medium text-slate-600">
                          Conversion date
                          <input
                            type="date"
                            value={line.chequeDate}
                            onChange={(e) => handleChequeChange(line.key, 'chequeDate', e.target.value)}
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </label>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="block text-sm font-medium text-slate-600">
                          Bank
                          <select
                            value={line.chequeBank}
                            onChange={(e) => handleChequeChange(line.key, 'chequeBank', e.target.value)}
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <option value="">Select bank…</option>
                            {SRI_LANKA_BANKS.map((b) => (
                              <option key={`${b.code}-${b.name}`} value={b.name}>
                                {b.name} ({b.code})
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-sm font-medium text-slate-600">
                          Bank code
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={4}
                            value={line.chequeBankCode}
                            onChange={(e) =>
                              handleChequeChange(line.key, 'chequeBankCode', e.target.value.replace(/\D/g, '').slice(0, 4))
                            }
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="e.g. 7056"
                          />
                        </label>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="block text-sm font-medium text-slate-600">
                          Branch code
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={3}
                            value={line.chequeBranchCode}
                            onChange={(e) =>
                              handleChequeChange(
                                line.key,
                                'chequeBranchCode',
                                e.target.value.replace(/\D/g, '').slice(0, 3),
                              )
                            }
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 font-mono text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="e.g. 080"
                          />
                        </label>
                        <label className="block text-sm font-medium text-slate-600">
                          Cheque number
                          <input
                            type="text"
                            autoComplete="off"
                            value={line.chequeNumber}
                            onChange={(e) => handleChequeChange(line.key, 'chequeNumber', e.target.value)}
                            disabled={line.chequeDeposited}
                            className="mt-1 w-full rounded-xl border-0 bg-white px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35 disabled:cursor-not-allowed disabled:opacity-60"
                            placeholder="e.g. 123456"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="flex flex-col gap-1 text-sm text-slate-600 sm:flex-row sm:flex-wrap sm:items-baseline">
                  <span>
                    Total payment:{' '}
                    <span className="font-semibold tabular-nums text-slate-900">
                      {money(paymentTotalPreview)}
                    </span>
                  </span>
                  {chequeTotalPreview > 0 ? (
                    <span className="text-xs text-slate-500 sm:ml-2">
                      (cheques: {money(chequeTotalPreview)})
                    </span>
                  ) : null}
                  {cdmTotalPreview > 0 ? (
                    <span className="text-xs text-slate-500 sm:ml-2">
                      (CDM: {money(cdmTotalPreview)})
                    </span>
                  ) : null}
                  {onlineTransferTotalPreview > 0 ? (
                    <span className="text-xs text-slate-500 sm:ml-2">
                      (online: {money(onlineTransferTotalPreview)})
                    </span>
                  ) : null}
                </p>
                <label className="block text-sm font-medium text-slate-600">
                  Note (optional)
                  <input
                    type="text"
                    value={form.note}
                    onChange={(e) => handleChange('note', e.target.value)}
                    className="mt-1 w-full rounded-xl border-0 bg-slate-100 px-3 py-2.5 text-sm ring-1 ring-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/35"
                    placeholder="e.g. Reference, remarks…"
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="shrink-0 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur-sm sm:px-6 sm:py-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50 sm:w-auto sm:py-2.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || customers.length === 0 || newPaymentsLocked}
                className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-md disabled:opacity-60 sm:w-auto sm:py-2.5"
              >
                {saving ? 'Saving…' : editPayment ? 'Save changes' : 'Save payment'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
